import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { validateAndSanitizeText } from '../utils/sanitization';

const ImageManager = () => {
  const { images, saveImage, deleteImage } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageName, setImageName] = useState('');
  const [imageDescription, setImageDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
      } else {
        alert('Please select an image file');
        event.target.value = '';
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !imageName.trim()) {
      alert('Please select a file and enter a name');
      return;
    }

    setUploading(true);
    try {
      // Sanitize inputs before saving
      await saveImage({
        file: selectedFile,
        name: validateAndSanitizeText(imageName.trim(), 200),
        description: validateAndSanitizeText(imageDescription.trim(), 1000),
        timestamp: new Date().toISOString()
      });

      // Reset form
      setSelectedFile(null);
      setImageName('');
      setImageDescription('');
      setShowAddForm(false);

      // Clear file input
      const fileInput = document.getElementById('image-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await deleteImage(imageId);
      } catch (error) {
        console.error('Error deleting image:', error);
        alert('Failed to delete image. Please try again.');
      }
    }
  };

  const openImageViewer = (image) => {
    setSelectedImage(image);
  };

  const closeImageViewer = () => {
    setSelectedImage(null);
  };

  return (
    <div className="image-manager">
      <div className="image-manager-header">
        <h2>📸 Image Gallery</h2>
        <button
          className="add-image-button"
          onClick={() => setShowAddForm(!showAddForm)}
          disabled={uploading}
        >
          {showAddForm ? '✖️ Cancel' : '➕ Add Image'}
        </button>
      </div>

      {showAddForm && (
        <div className="add-image-form">
          <h3>Add New Image</h3>
          <div className="form-group">
            <label htmlFor="image-file-input">Select Image:</label>
            <input
              id="image-file-input"
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="image-name">Name:</label>
            <input
              id="image-name"
              type="text"
              value={imageName}
              onChange={(e) => setImageName(e.target.value)}
              placeholder="Enter image name"
              disabled={uploading}
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="image-description">Description:</label>
            <textarea
              id="image-description"
              value={imageDescription}
              onChange={(e) => setImageDescription(e.target.value)}
              placeholder="Enter image description (optional)"
              disabled={uploading}
              maxLength={500}
              rows={3}
            />
          </div>

          <div className="form-actions">
            <button
              className="upload-button"
              onClick={handleUpload}
              disabled={!selectedFile || !imageName.trim() || uploading}
            >
              {uploading ? '⏳ Uploading...' : '📤 Upload Image'}
            </button>
            <button
              className="cancel-button"
              onClick={() => setShowAddForm(false)}
              disabled={uploading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="images-grid">
        {images.length === 0 ? (
          <div className="no-images">
            <p>No images uploaded yet.</p>
            <p>Click "Add Image" to get started!</p>
          </div>
        ) : (
          images.map((image) => (
            <div key={image.id} className="image-card">
              <div className="image-preview" onClick={() => openImageViewer(image)}>
                <img
                  src={image.url}
                  alt={image.name}
                  loading="lazy"
                />
              </div>
              <div className="image-info">
                <h4 className="image-name">{image.name}</h4>
                {image.description && (
                  <p className="image-description">{image.description}</p>
                )}
                <div className="image-meta">
                  <small>
                    {new Date(image.timestamp).toLocaleDateString()} {new Date(image.timestamp).toLocaleTimeString()}
                  </small>
                </div>
                <div className="image-actions">
                  <button
                    className="view-button"
                    onClick={() => openImageViewer(image)}
                  >
                    👁️ View
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(image.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedImage && (
        <div className="image-viewer-overlay" onClick={closeImageViewer}>
          <div className="image-viewer" onClick={(e) => e.stopPropagation()}>
            <div className="image-viewer-header">
              <h3>{selectedImage.name}</h3>
              <button className="close-button" onClick={closeImageViewer}>
                ✖️
              </button>
            </div>
            <div className="image-viewer-content">
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="full-image"
              />
              {selectedImage.description && (
                <div className="image-details">
                  <h4>Description:</h4>
                  <p>{selectedImage.description}</p>
                </div>
              )}
              <div className="image-metadata">
                <p><strong>Added:</strong> {new Date(selectedImage.timestamp).toLocaleDateString()} {new Date(selectedImage.timestamp).toLocaleTimeString()}</p>
                {selectedImage.size && (
                  <p><strong>Size:</strong> {(selectedImage.size / (1024 * 1024)).toFixed(2)} MB</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageManager;